import { useState, type FormEvent } from "react";
import { asDecimalString, Money, type PaymentMethodOut, type PaymentOut } from "@shop-ledger/shared-types";
import { Button, DataTable, Input, usePagination, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { listPaymentMethods, listPayments, recordPayment } from "../../api/paymentsApi";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import { BranchSelect } from "../../components/BranchSelect";

const TENANT_CURRENCY_FALLBACK = "UGX"; // Tenant.currency isn't returned by GET /auth/me
// (UserOut, `@shop-ledger/shared-types`'s api/auth.ts) — same documented stand-in every
// other money-displaying page in this app uses (see e.g. DashboardPage.tsx).

const METHOD_COLUMNS: DataTableColumn<PaymentMethodOut, keyof PaymentMethodOut>[] = [
  { key: "code", header: "Code", render: (r) => r.code },
  { key: "display_name", header: "Display Name", render: (r) => r.display_name },
  { key: "provider", header: "Provider", render: (r) => r.provider },
  { key: "requires_reconciliation", header: "Async?", render: (r) => (r.requires_reconciliation ? "Yes" : "No (confirms instantly)") },
  { key: "is_enabled", header: "Enabled", render: (r) => (r.is_enabled ? "Yes" : "No") },
];

const PAYMENT_COLUMNS: DataTableColumn<PaymentOut, keyof PaymentOut>[] = [
  { key: "id", header: "Payment", render: (r) => <span className="muted">{r.id.slice(0, 8)}</span> },
  {
    key: "sale_id",
    header: "Against",
    render: (r) => (r.sale_id ? <span>Sale {r.sale_id.slice(0, 8)}</span> : r.invoice_or_bill_id ? <span>Invoice {r.invoice_or_bill_id.slice(0, 8)}</span> : "—"),
  },
  { key: "amount", header: "Amount", align: "right", render: (r) => Money.format(r.amount, TENANT_CURRENCY_FALLBACK) },
  {
    key: "status",
    header: "Status",
    render: (r) => (
      <span className={`badge ${r.status === "CONFIRMED" ? "badge--current" : r.status === "FAILED" ? "badge--overdue" : "badge--due-soon"}`}>{r.status}</span>
    ),
  },
  {
    key: "external_reference",
    header: "Reference",
    // external_reference on a CASH payment is really an internal idempotency key
    // ("<uuid>:CASH:<uuid>") — meaningless plumbing to a business owner if shown in full
    // (real issue found in a live audit). Truncate with the full value on hover so a
    // genuinely useful reference (e.g. a bank transfer id) is still recoverable.
    render: (r) =>
      r.external_reference ? (
        <span className="muted" title={r.external_reference}>
          {r.external_reference.length > 20 ? `${r.external_reference.slice(0, 20)}…` : r.external_reference}
        </span>
      ) : (
        "—"
      ),
  },
  { key: "received_at", header: "Received", render: (r) => (r.received_at ? new Date(r.received_at).toLocaleString() : "—") },
];

/**
 * §6.7's Payments surface. Known gap now fixed (§10.5/§10.6): `GET /payments` is a real
 * itemized list — this replaces the previous denormalized-invoice-aggregate stand-in
 * (an earlier pass here fell back to `InvoicesPage`'s `amount_paid`/`amount_due` columns
 * because no list route existed at all).
 */
export function PaymentsPage() {
  const { ensureFreshToken } = useAuth();
  const methodsState = useAsyncData(() => ensureFreshToken().then((token) => listPaymentMethods(token)), []);
  const paymentsState = useAsyncData(() => ensureFreshToken().then((token) => listPayments(token)), []);
  const { pageData: paymentsPageData, pagination: paymentsPagination, setPage: setPaymentsPage } = usePagination(
    paymentsState.data ?? [],
    25,
  );

  const [methodCode, setMethodCode] = useState("");
  const [amount, setAmount] = useState("");
  const [targetKind, setTargetKind] = useState<"sale" | "invoice">("invoice");
  const [targetId, setTargetId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      const payment = await recordPayment(token, {
        payment_method_code: methodCode,
        amount: asDecimalString(amount),
        sale_id: targetKind === "sale" ? targetId : undefined,
        invoice_or_bill_id: targetKind === "invoice" ? targetId : undefined,
        branch_id: branchId || undefined,
      });
      setSuccess(`Payment ${payment.id} recorded — status: ${payment.status}.`);
      setAmount("");
      setTargetId("");
      paymentsState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not record payment.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Payments</h1>
          <p className="page__subtitle">Payment methods and manual payment recording.</p>
        </div>
      </div>

      {methodsState.error ? <StatusBanner kind="error">{methodsState.error}</StatusBanner> : null}

      <div className="panel">
        <h2 className="panel__title">Enabled payment methods</h2>
        {methodsState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={METHOD_COLUMNS}
            rows={methodsState.data ?? []}
            getRowId={(row) => row.id}
            emptyMessage="No payment methods configured."
          />
        )}
      </div>

      <div className="panel">
        <h2 className="panel__title">Record a payment</h2>
        <p className="muted">
          Against a sale or an invoice/bill by id — exactly one of the two, matching the backend's
          <code> ck_payment_single_target</code> constraint.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="sl-field">
              <label className="sl-field__label" htmlFor="pay-method">
                Method code
              </label>
              <select id="pay-method" className="sl-input" value={methodCode} onChange={(e) => setMethodCode(e.target.value)} required>
                <option value="">Select…</option>
                {(methodsState.data ?? []).map((m) => (
                  <option key={m.id} value={m.code}>
                    {m.display_name} ({m.code})
                  </option>
                ))}
              </select>
            </div>
            <Input label="Amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} required />
            <div className="sl-field">
              <label className="sl-field__label" htmlFor="pay-target-kind">
                Applies to
              </label>
              <select
                id="pay-target-kind"
                className="sl-input"
                value={targetKind}
                onChange={(e) => setTargetKind(e.target.value as "sale" | "invoice")}
              >
                <option value="invoice">Invoice/Bill ID</option>
                <option value="sale">Sale ID</option>
              </select>
            </div>
            <Input label={targetKind === "invoice" ? "Invoice/Bill ID" : "Sale ID"} value={targetId} onChange={(e) => setTargetId(e.target.value)} required />
            <BranchSelect id="pay-branch" label="Branch (optional)" value={branchId} onChange={setBranchId} allOptionLabel="No branch" />
          </div>
          {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
          {success ? <StatusBanner kind="info">{success}</StatusBanner> : null}
          <div className="form-actions">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Recording…" : "Record payment"}
            </Button>
          </div>
        </form>
      </div>

      <div className="panel">
        <div className="page__header">
          <h2 className="panel__title">All payments</h2>
          <Button variant="secondary" onClick={paymentsState.reload}>
            Refresh
          </Button>
        </div>
        {paymentsState.error ? <StatusBanner kind="error">{paymentsState.error}</StatusBanner> : null}
        {paymentsState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={PAYMENT_COLUMNS}
            rows={paymentsPageData}
            getRowId={(row) => row.id}
            pagination={paymentsPagination}
            onPageChange={setPaymentsPage}
            emptyMessage="No payments recorded yet."
            caption="Payments"
          />
        )}
      </div>
    </div>
  );
}
